"use client";

import { useQuery } from "@tanstack/react-query";
import type { Org } from "@/lib/orgs/types";
import { safeFetch } from "@/lib/api/safe-fetch";
import { MyOrgsResponse } from "@ripenn/api-contracts/webapp/orgs";
import { orgKeys } from "@/lib/queryKeys";

export interface UseMyOrgsResult {
  orgs: Org[];
  loading: boolean;
}

/**
 * Fetches the current user's orgs from `/api/me/orgs`. Shared
 * across the sidebar and the ProjectSwitcher; the single TanStack
 * cache slot (`orgKeys.myOrgs()`) means one network call no matter
 * how many components mount the hook.
 *
 * Failure mode: returns an empty list and clears `loading`. Org
 * chrome should degrade gracefully rather than break the layout.
 */
export function useMyOrgs(): UseMyOrgsResult {
  const { data, isPending } = useQuery({
    queryKey: orgKeys.myOrgs(),
    queryFn: () => safeFetch(MyOrgsResponse, "/api/me/orgs"),
    // Non-critical chrome: settle fast into the empty-list fallback
    // instead of retry-looping while the sidebar shows a spinner.
    retry: 1,
  });

  return { orgs: data ?? [], loading: isPending };
}
